import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../../api/apiClient";
import { printAgent } from "@/lib/print-agent";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { AsyncReactSelect, ReactSelect } from "@/components/ui/react-select";
import {
  PRINTER_ARCHETYPES,
  CONNECTION_TYPES,
  STATUS_OPTIONS,
} from "./printerConstants";
import {
  Printer,
  RefreshCw,
  Sparkles,
  Bot,
  AlertCircle,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function PrinterFormDialog({
  open,
  onClose,
  onSubmit,
  editMode,
  form,
  setForm,
  isPending,
}) {
  const currentStore = useSelector((state) => state.current_store);
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (p) => {
    if (!permissions) return false;
    if (Array.isArray(p)) return p.some((perm) => permissions.includes(perm));
    return permissions.includes(p);
  };

  const canCreate = hasPermission("printer create");
  const canEdit = hasPermission("printer update");

  // Query printers from ERF Print Agent
  const {
    data: agentResult,
    isLoading: agentLoading,
    refetch: refetchAgentPrinters,
  } = useQuery({
    queryKey: [
      "print-agent-printers",
      form.store_id?.value,
      currentStore?.id,
    ],
    queryFn: async () => {
      let key = currentStore?.print_agent_pairing_key;
      if (form.store_id?.value && form.store_id.value !== currentStore?.id) {
        try {
          const res = await apiClient.get(`/store/${form.store_id.value}`);
          key = res.data?.data?.print_agent_pairing_key || key;
        } catch {
          // ignore
        }
      }
      return await printAgent.getPrinters({ pairingKey: key });
    },
    enabled: Boolean(
      open && (canCreate || canEdit) && (form.connection_type === "agent" || form.connection_type === "usb" || form.connection_type === "network")
    ),
    retry: false,
    staleTime: 10000,
  });

  const detectedPrinters = useMemo(
    () => agentResult?.printers || [],
    [agentResult?.printers]
  );
  const isAgentOnline = detectedPrinters.length > 0;

  // Options for discovered printers
  const addressOptions = useMemo(() => {
    const opts = [];
    const seenValues = new Set();

    detectedPrinters.forEach((p) => {
      const printerName = p.name || p.displayName;
      const portVal = p.port || p.address || p.name;
      const portClean =
        portVal && portVal !== printerName && !seenValues.has(portVal)
          ? ` (${portVal})`
          : "";
      const defaultTag = p.isDefault ? " [Default]" : "";
      const statusTag = p.statusText ? ` • ${p.statusText}` : "";

      if (printerName && !seenValues.has(printerName)) {
        seenValues.add(printerName);
        opts.push({
          value: printerName,
          label: `${printerName}${portClean}${defaultTag}${statusTag}`,
          printer: p,
        });
      }

      if (
        portVal &&
        portVal !== printerName &&
        !seenValues.has(portVal) &&
        (p.connectionType === "network" || portVal.includes("."))
      ) {
        seenValues.add(portVal);
        opts.push({
          value: portVal,
          label: `IP: ${portVal} (${printerName})`,
          printer: p,
        });
      }
    });

    if (
      form.address &&
      !seenValues.has(form.address) &&
      form.address.trim() !== ""
    ) {
      opts.unshift({
        value: form.address,
        label: `${form.address} (Custom)`,
        printer: null,
      });
    }

    return opts;
  }, [detectedPrinters, form.address]);

  const currentAddressOption = useMemo(() => {
    if (!form.address) return null;
    return (
      addressOptions.find((o) => o.value === form.address) || {
        value: form.address,
        label: form.address,
      }
    );
  }, [form.address, addressOptions]);

  const handleAddressSelect = (selectedOption) => {
    const val = selectedOption?.value || "";
    const chosenPrinter = selectedOption?.printer;

    setForm((prev) => {
      const updates = { ...prev, address: val };

      if (!prev.name.trim() && chosenPrinter) {
        updates.name = chosenPrinter.displayName || chosenPrinter.name || "";
      }

      if (chosenPrinter?.connectionType) {
        const agentType = chosenPrinter.connectionType.toLowerCase();
        if (
          ["network", "usb", "bluetooth", "agent"].includes(agentType) &&
          prev.connection_type !== agentType
        ) {
          updates.connection_type = agentType;
        }
      }

      return updates;
    });
  };

  const loadStoreOptions = async (inputValue) => {
    try {
      const res = await apiClient.get("/store", {
        params: { search: inputValue, perPage: 20 },
      });
      return (res.data?.data ?? []).map((s) => ({
        value: s.id,
        label: s.name,
      }));
    } catch {
      return [];
    }
  };

  const handleSelectArchetype = (arch) => {
    setForm((prev) => ({
      ...prev,
      archetypeId: arch.id,
      connection_type: arch.connection || prev.connection_type,
      name: prev.name.trim() === "" || PRINTER_ARCHETYPES.some((a) => a.defaultName === prev.name)
        ? arch.defaultName
        : prev.name,
      autoCreatePaper: arch.id !== "custom",
    }));
  };

  const currentArchetype = PRINTER_ARCHETYPES.find(
    (a) => a.id === (form.archetypeId || "pos_thermal")
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden bg-background">
        <DialogHeader className="px-6 py-4 border-b border-border bg-card shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
              <Printer className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                {editMode ? "Edit Printer" : "Register New Printer"}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                {editMode
                  ? "Update hardware connection and store settings"
                  : "Choose a printer type for 1-click auto-setup or configure custom hardware"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Quick Archetype Setup Selector (Only shown in Add mode) */}
          {!editMode && (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  1. Choose Printer Type (Quick Setup)
                </Label>
                <span className="text-[11px] text-muted-foreground">
                  Pre-configures paper & documents
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {PRINTER_ARCHETYPES.map((arch) => {
                  const Icon = arch.icon;
                  const isSelected = (form.archetypeId || "pos_thermal") === arch.id;

                  return (
                    <button
                      key={arch.id}
                      type="button"
                      onClick={() => handleSelectArchetype(arch)}
                      className={cn(
                        "flex flex-col items-start p-3 rounded-xl border text-left transition-all duration-150 cursor-pointer relative",
                        isSelected
                          ? "border-primary bg-primary/5 shadow-xs ring-1 ring-primary/30"
                          : "border-border/80 hover:border-border hover:bg-muted/40"
                      )}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="h-2.5 w-2.5" />
                        </div>
                      )}
                      <div
                        className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-lg mb-2",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="text-xs font-semibold text-foreground line-clamp-1">
                        {arch.shortLabel}
                      </span>
                      <span className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2 leading-tight">
                        {arch.description}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Archetype Benefit Banner */}
              {currentArchetype && currentArchetype.id !== "custom" && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-2.5 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <Sparkles className="h-3.5 w-3.5 shrink-0" />
                    <span>{currentArchetype.highlight}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="auto-paper-switch"
                      className="text-[11px] text-muted-foreground font-normal cursor-pointer"
                    >
                      Auto-create paper:
                    </Label>
                    <Switch
                      id="auto-paper-switch"
                      checked={form.autoCreatePaper !== false}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, autoCreatePaper: checked }))
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Store & Printer Name */}
          <div className="space-y-4">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {!editMode ? "2. Printer Identification" : "Printer Details"}
            </Label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="store-select" className="text-xs">
                  Store <span className="text-destructive">*</span>
                </Label>
                <AsyncReactSelect
                  id="store-select"
                  loadOptions={loadStoreOptions}
                  defaultOptions
                  value={form.store_id}
                  onChange={(val) => setForm((prev) => ({ ...prev, store_id: val }))}
                  placeholder="Select store…"
                  isDisabled={Boolean(currentStore?.id) && !form.store_id}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="printer-name" className="text-xs">
                  Printer Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="printer-name"
                  placeholder="e.g. Counter Thermal Receipt"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Connection & Address */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {!editMode ? "3. Connection & Hardware" : "Hardware Connection"}
              </Label>
              <div className="flex items-center gap-2">
                {agentLoading ? (
                  <Badge variant="outline" className="text-[10px] py-0 px-2 gap-1 text-muted-foreground font-normal">
                    <Spinner className="h-2.5 w-2.5" /> Checking Agent…
                  </Badge>
                ) : isAgentOnline ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] py-0 px-2 gap-1 text-emerald-600 border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/30 font-medium cursor-pointer"
                    onClick={() => refetchAgentPrinters()}
                    title="Click to refresh printers discovered by ERF Print Agent"
                  >
                    <Bot className="h-3 w-3" /> Agent Online ({detectedPrinters.length})
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="text-[10px] py-0 px-2 gap-1 text-muted-foreground border-border bg-muted/30 font-normal cursor-pointer"
                    onClick={() => refetchAgentPrinters()}
                    title="ERF Print Agent not detected. Click to recheck."
                  >
                    <Bot className="h-3 w-3" /> Agent Offline
                  </Badge>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={() => refetchAgentPrinters()}
                  title="Scan for installed printers"
                >
                  <RefreshCw className={cn("h-3 w-3", agentLoading && "animate-spin")} />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Connection Type <span className="text-destructive">*</span>
                </Label>
                <ReactSelect
                  options={CONNECTION_TYPES.map((c) => ({
                    value: c.value,
                    label: c.label,
                  }))}
                  value={
                    CONNECTION_TYPES.map((c) => ({
                      value: c.value,
                      label: c.label,
                    })).find((c) => c.value === form.connection_type) || null
                  }
                  onChange={(opt) =>
                    setForm((prev) => ({
                      ...prev,
                      connection_type: opt?.value || "network",
                    }))
                  }
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="printer-address" className="text-xs">
                    Address / Discovered Device
                  </Label>
                  {isAgentOnline && (
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                      Select installed printer
                    </span>
                  )}
                </div>
                {addressOptions.length > 0 ? (
                  <ReactSelect
                    options={addressOptions}
                    value={currentAddressOption}
                    onChange={handleAddressSelect}
                    placeholder={
                      form.connection_type === "network"
                        ? "192.168.1.100:9100 or select printer…"
                        : "Select installed printer or enter identifier…"
                    }
                    isClearable
                  />
                ) : (
                  <Input
                    id="printer-address"
                    placeholder={
                      CONNECTION_TYPES.find((c) => c.value === form.connection_type)
                        ?.placeholder || "Address or identifier…"
                    }
                    value={form.address}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, address: e.target.value }))
                    }
                  />
                )}
              </div>
            </div>

            {!isAgentOnline && form.connection_type === "agent" && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  The <strong>ERF Print Agent</strong> desktop application is not currently active on this machine. You can enter the port or address manually.
                </span>
              </div>
            )}
          </div>

          {/* Status & Default Settings */}
          <div className="pt-2 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <ReactSelect
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((s) => s.value === form.status)}
                onChange={(opt) =>
                  setForm((prev) => ({ ...prev, status: opt?.value || "active" }))
                }
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20">
              <div>
                <Label htmlFor="printer-default" className="text-xs font-medium cursor-pointer">
                  Default Printer for Store
                </Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Used as fallback for all unassigned documents
                </p>
              </div>
              <Switch
                id="printer-default"
                checked={Boolean(form.is_default)}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, is_default: checked }))
                }
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-border bg-card flex flex-row items-center justify-between sm:justify-between shrink-0">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSubmit}
            disabled={
              isPending ||
              !form.name.trim() ||
              !form.store_id?.value ||
              (editMode ? !canEdit : !canCreate)
            }
            className="gap-1.5"
          >
            {isPending ? (
              <>
                <Spinner className="h-3.5 w-3.5" />
                Saving…
              </>
            ) : editMode ? (
              "Save Changes"
            ) : (
              <>
                <Check className="h-3.5 w-3.5" />
                Register & Complete Setup
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
